import {
	Vue, Component, Watch, Prop, toNative,
} from 'vue-facing-decorator';
import * as DB from 'interfaces/database';
import { httpClient } from 'utils/http';
import { TabEvent } from 'interfaces/app';
import { useRoute } from 'vue-router';
import CustomSelect from 'components/CustomSelect';
import CustomCheckbox from 'components/CustomCheckbox';
import TemplatePosition from '../TemplatePosition';
import Template from './template.vue';

@Component({
	components: {
		TemplatePosition,
		CustomSelect,
		CustomCheckbox,
	},
	emits: ['routeTemplateState'],
	mixins: [Template],
})
class EditTemplatePositionEdit extends Vue {
	private singleTemplatePosition = {} as DB.TemplatePositionPhotoModel | DB.TemplatePositionTextModel;

	private loaded = false;

	private visible = false;

	private spinner = false;

	private index = 0;

	private rowHeight = 60;

	private isLoading = false;

	private borderImage: DB.BorderImageModel[] = [];

	private font: DB.FontModel[] = [];

	private options = ['photo', 'text'];

	private mask = ['circle', 'heart', 'diamond', 'brush', 'cloud', 'star', 'flower', 'stamp', 'octagon', 'clover'];

	private route = useRoute();

	@Prop({
		type: String,
		required: true,
	})
	public readonly templatePositionId!: string;

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected get isPhotoRoute(): boolean {
		return this.route.query.type === 'photo';
	}

	protected mounted(): void {
		this.getSingleTemplate();
		this.getData();
	}

	@Watch('loggedIn')
	private async getSingleTemplate(): Promise<DB.TemplatePositionPhotoModel | DB.TemplatePositionTextModel | undefined> {
		if (this.loggedIn) {
			// set the loading state
			this.isLoading = true;
			const url = this.route.query.type === 'photo' ? 'templatepositionphoto' : 'templatepositiontext';
			try {
				const resp = await httpClient.get<DB.TemplatePositionPhotoModel | DB.TemplatePositionTextModel>(`/api/${url}/${this.route.params.positionid}`);
				this.singleTemplatePosition = resp.data;
				// eslint-disable-next-line consistent-return
				return this.singleTemplatePosition;
			} catch (error: any) {
				this.$toastError(error.message);
			} finally {
				this.isLoading = false;
			}
		}
		return undefined;
	}

	@Watch('loggedIn')
	private getData(): void {
		if (this.loggedIn) {
			// set the loading state
			this.isLoading = true;
			const parameter = new URLSearchParams({
				limit: '0',
			});
			Promise
				.all([
					httpClient.get<DB.BorderImageModel[]>(`/api/borderimage?${parameter}`),
					httpClient.get<DB.FontModel[]>('/api/font?limit=0'),
				])
				.then(([borderResponse, fontResponse]) => {
					this.borderImage = borderResponse.data;
					this.font = fontResponse.data;
					return null;
				}).catch((error) => {
					this.$toastError(error.message);
				});
		}
		return undefined;
	}

	protected async editTemplate(): Promise<DB.TemplatePositionPhotoModel | DB.TemplatePositionTextModel | null> {
		if (this.loggedIn) {
			try {
				this.isLoading = true;
				const url = this.route.query.type === 'photo' ? 'templatepositionphoto' : 'templatepositiontext';
				const { data } = await httpClient.put<DB.TemplatePositionPhotoModel | DB.TemplatePositionTextModel>(
					`/api/${url}/${this.templatePositionId}`,
					{ ...this.singleTemplatePosition },
				);
				this.$toastSuccess('Template Position Edited successfully');
				return data;
			} catch (error: any) {
				this.$toastError(error.message);
			} finally {
				this.isLoading = false;
			}
		}
		return Promise.resolve(null);
	}

	protected routeTemplateState(data: TabEvent<DB.TemplateModel>['editItem']): void {
		this.$emit(
			'routeTemplateState',
			{
				data,
			},
		);
	}
}

export default toNative(EditTemplatePositionEdit);
